import { styled } from '@/src/stitches.config'
import Button from '@/src/common/components/marketing/buttons/Button'
import { useState } from 'react'
import { ManualAddressForm } from '@/src/modules/pg-plus/redemption/forms/ManualAddressForm'
import { SmartyForm } from '@/src/modules/smarty/SmartyForm'
import { usePGPlusStepAndModalContext } from '@/src/common/contexts/PGPlusModalAndStepContext'
import usePostGiftRedeemPlastic from '@/src/common/hooks/usePostGiftRedeemPlastic'
import { DISPATCH_SET_STEP } from '@/src/common/reducers/constants'
import {
    GIFT_DETAILS_STEP,
    SUCCESS_STEP,
} from '@/src/modules/pg-plus/redemption/constants'
import { addressValidationSchema } from '@/src/modules/checkout/schemas'
import { useRecipientDeliveryContext } from '@/src/common/contexts/RecipientDeliveryContext'
import { FAQ_PAGE } from '../../constants'

const StyledForm = styled('form', {
    width: 425,
    maxWidth: '100%',
    '& button': {
        mt: 10,
    },
})

interface IForm {
    setIsRedeemed?: Function | null
    showSuccessStep: boolean
    refetchGift: Function
}

const PlasticExchangeAddressForm = ({
    setIsRedeemed,
    showSuccessStep,
    refetchGift,
}: IForm) => {
    const { dispatch, giftState } = usePGPlusStepAndModalContext()
    const { cardUuid } = giftState
    const {
        name,
        address1,
        address2,
        city,
        state,
        zip,
        setFormErrors,
        searchManually,
    } = useRecipientDeliveryContext()
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const { mutate: postRedeem, isError: postIsError } =
        usePostGiftRedeemPlastic()

    const getFullName = (nameString: string) => {
        const nameArray = nameString.trim().split(' ')
        let nameArrayTrimmed = []
        for (const nm of nameArray) {
            if (nm.length > 0) {
                nameArrayTrimmed.push(nm.trim())
            }
        }
        if (nameArrayTrimmed.length < 2) return null
        const lastName = nameArrayTrimmed[nameArrayTrimmed.length - 1]
        nameArrayTrimmed.pop()
        const firstName = nameArrayTrimmed.join(' ')
        return { firstName: firstName, lastName: lastName }
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault()
        setIsLoading(true)
        setFormErrors([])

        const isFormValid = await addressValidationSchema.isValid(
            {
                name,
                address1,
                address2,
                city,
                state,
                zip,
            },
            { abortEarly: false }
        )

        if (!isFormValid) {
            addressValidationSchema
                .validate(
                    {
                        name,
                        address1,
                        address2,
                        city,
                        state,
                        zip,
                    },
                    { abortEarly: false }
                )
                .then(() => {
                    setFormErrors([])
                })
                .catch((err) => {
                    console.error(err)
                    setFormErrors(err.inner)
                    setIsLoading(false)
                })
            return
        }

        const nameValues = getFullName(name)
        if (!nameValues) {
            return
        } // this should never happen due to validation above

        const payload = {
            card_uuid: cardUuid,
            first_name: nameValues.firstName,
            last_name: nameValues.lastName,
            address1: address1,
            address2: address2,
            city: city,
            state: state,
            zip: zip,
        }

        postRedeem(payload, {
            onSuccess: () => {
                if (setIsRedeemed) {
                    setIsRedeemed(true)
                }

                setTimeout(() => {
                    refetchGift()
                    dispatch({
                        type: DISPATCH_SET_STEP,
                        payload: {
                            stepName: showSuccessStep
                                ? SUCCESS_STEP
                                : GIFT_DETAILS_STEP,
                        },
                    })
                }, 3000)
            },
            onError: () => {
                setFormErrors(e)
                console.error(e)
                setIsLoading(false)
            },
        })
    }

    return (
        <StyledForm id="delivery-form" onSubmit={handleSubmit}>
            <p className={'header margin-top'}>Mail to:</p>
            {searchManually ? (
                <ManualAddressForm />
            ) : (
                <SmartyForm showShippingOptions={false} />
            )}
            {postIsError && (
                <p className={'error'}>
                    Error: Please try again. If issues persist, see our list of{' '}
                    <a href={FAQ_PAGE}>frequently asked questions</a>.
                </p>
            )}
            <Button
                label={isLoading ? 'Submitting' : 'Submit'}
                textTransform={'uppercase'}
                variant={'primary'}
                type={'submit'}
                width={'full'}
                showArrow={true}
                data-testid={'checkout-button'}
                isLoading={isLoading}
                disabled={isLoading}
            />
        </StyledForm>
    )
}

export default PlasticExchangeAddressForm
